import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Shimmer from './Shimmer';

// Per-section shimmers for the Dashboard (My Reports page). The page has
// three data-driven blocks on top of a static forest-green header:
//   • 4-up KPI tiles              → useListBookingsQuery + useListReportsQuery
//   • My Bookings list            → useListBookingsQuery
//   • My Reports table            → useListReportsQuery
// Header / search-filter bar / section titles render immediately so only
// these blocks get skeletoned.

// KPI tile — emoji + big serif number + small label.
export const DashboardKpisShimmer = () => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {Array.from({ length: 4 }).map((_, i) => (
      <Grid key={i} item xs={3}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '20px',
            p: 3,
            border: '1px solid var(--border-l, #EDE9E1)',
            boxShadow: '0 4px 20px rgba(21,42,30,.06)',
            height: '100%',
          }}
        >
          <Shimmer width={32} height={28} rounded={6} sx={{ mb: 1.5 }} />
          <Shimmer width={`${50 + (i * 13) % 30}%`} height={36} rounded={6} sx={{ mb: 1.25 }} />
          <Shimmer width="70%" height={11} rounded={4} />
        </Box>
      </Grid>
    ))}
  </Grid>
);

// Booking row — 44px icon · id/items stack · schedule · total · status+track.
// Matches the 5-col grid in the real card so the layout doesn't jump when
// data lands.
const BookingRowShimmer = ({ last = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '44px 1.6fr 1.3fr 1fr 130px' },
      alignItems: 'center',
      gap: { xs: 1, sm: 2 },
      px: 3, py: 2.25,
      borderBottom: last ? 'none' : '1px solid var(--border-l)',
    }}
  >
    <Shimmer
      width={40}
      height={40}
      rounded={10}
      sx={{ display: { xs: 'none', sm: 'block' } }}
    />
    <Box sx={{ minWidth: 0 }}>
      <Shimmer width={90} height={10} rounded={4} sx={{ mb: 0.75 }} />
      <Shimmer width="80%" height={13} rounded={4} />
    </Box>
    <Box>
      <Shimmer width={60} height={9} rounded={4} sx={{ mb: 0.5 }} />
      <Shimmer width={130} height={12} rounded={4} />
    </Box>
    <Box>
      <Shimmer width={40} height={9} rounded={4} sx={{ mb: 0.5 }} />
      <Shimmer width={80} height={16} rounded={6} />
    </Box>
    <Stack spacing={1} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
      <Shimmer width={80} height={20} rounded={100} />
      <Shimmer width={88} height={28} rounded={100} />
    </Stack>
  </Box>
);

export const DashboardBookingsShimmer = ({ count = 3 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <BookingRowShimmer key={i} last={i === count - 1} />
    ))}
  </>
);

// Report row — 48px icon · test-name/code · date · centre · status · view.
// Uses the same 6-col grid as the real row.
const ReportRowShimmer = ({ last = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '48px 1fr 140px 140px 120px 110px',
      px: 3,
      py: 1.75,
      borderBottom: last ? 'none' : '1px solid var(--border-l, #EDE9E1)',
      alignItems: 'center',
    }}
  >
    <Shimmer width={36} height={36} rounded={10} />
    <Box sx={{ minWidth: 0, pr: 2 }}>
      <Shimmer width="75%" height={13} rounded={4} sx={{ mb: 0.75 }} />
      <Shimmer width={110} height={10} rounded={4} />
    </Box>
    <Shimmer width={100} height={12} rounded={4} />
    <Shimmer width={120} height={12} rounded={4} />
    <Shimmer width={80} height={20} rounded={100} />
    <Shimmer width={76} height={28} rounded={100} />
  </Box>
);

export const DashboardReportsShimmer = ({ count = 5 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <ReportRowShimmer key={i} last={i === count - 1} />
    ))}
  </>
);
